'use strict';

define('vb/private/stateManagement/extensionViewModel',[
  'vb/private/log',
  'vb/private/constants',
  'vb/private/utils',
  'vb/private/stateManagement/stateUtils',
  'vb/private/stateManagement/extensionScope',
], (Log, Constants, Utils, StateUtils, ExtensionScope) => {
  const logger = Log.getLogger('/vb/stateManagement/extensionViewModel');

  /**
   * @typedef {import('vb/private/stateManagement/container').default} Container
   * @typedef {import('vb/private/stateManagement/application').default} Application
   * @typedef {import('vb/private/vx/extension').default} Extension
   * @typedef {import('vb/private/helpers/runtimeEnvironment').default} RuntimeEnvironment
   */

  const UI_SELF = `${Constants.DefaultPaths.UI}${Constants.ExtensionFolders.SELF}/`;

  /**
   * Matches a pattern like below
   * - 'vx/foo/ui/self/resources/*'
   */
  const VX_EXT_UISELF_RESOURCES_REGEXP = /^(.*vx\/)([^/]*)\/ui\/self\/resources\/(.*)$/g;

  /**
   * Matches a pattern like below
   * - 'vx/foo/ui/self/*'
   */
  const VX_EXT_UISELF_REGEXP = /^(.*vx\/)([^/]*)\/ui\/self\/(.*)$/g;

  const checkInvalidPath = (p) => (p.match(VX_EXT_UISELF_REGEXP) && !p.match(VX_EXT_UISELF_RESOURCES_REGEXP));

  /**
   * used to check if the descriptor file itself exists in manifest.
   *
   * @param {string} mp the path to extension descriptor
   * @param {Extension} ext
   * @returns {string|undefined} the validated path or undefined
   */
  const validatePath = (mp, ext) => {
    const extension = ext;
    const { id } = ext;
    try {
      extension.fileExists(mp);
    // eslint-disable-next-line no-unused-vars
    } catch (e) {
      const msg = `An extension descriptor for the extension '${id}' was not found.`;
      logger.info(msg);
      return undefined;
    }
    return mp;
  };

  /**
   * Loads an extension's descriptor file and caches the same in the extension model.
   * @param {Extension} ext
   * @param {RuntimeEnvironment} re
   * @returns {Promise<void>} that resolves when the definition is loaded, or if a resource is not found
   */
  const loadDescriptor = (ext, re) => Promise.resolve().then(() => {
    if (ext && ext.extensionResources && !ext.extensionResources.definition) {
      const e = ext;
      const descPath = `${UI_SELF}extension.json`;

      if (validatePath(descPath, e)) {
        // load an extension descriptor once and cache the definition
        const fullPath = `${e.baseUrl}${descPath}`;
        return re.getTextResource(fullPath)
          .then((extJSON) => {
            e.extensionResources.definition = Utils.parseJsonResource(extJSON, fullPath);
            return undefined;
          });
      }
      e.extensionResources.definition = {}; // default definition
    }
    return undefined;
  });

  /**
   * Represents the view model layer of an extension that deals with loading and managing extension specific state
   * (different from src/vb/private/vx/extension.js, is the model for an extension digest).
   * Example, when a container loads, this class ensures that extension level types are loaded and referenceable
   * from all scopes a container exposes, including other dependent extensions, for the current extension's tree.
   *
   * Note: The first container in an extension that loads, can initiate the construction of this class and set up
   * the view model for the extension.
   */
  class ExtensionViewModel {
    /**
     * Load an extension descriptor file, when a container begins to load its resources. Additionally load descriptors
     * for all dependencies for the current extension.
     * Once a descriptor is loaded, the 'extension.extensionResources.definition' object is populated. Currently only
     * 'types' and 'interface.types' sub-properties are allowed in the descriptor.
     *
     * @param {Object} container
     * @param {Extension} container.extension the extension model
     * @param {Application} container.application, generally an appUi
     * @returns {Promise} resolves when the extension descriptor is loaded, or if loading is skipped
     */
    static loadExtensionDescriptor({ extension, application }) {
      return Promise.resolve().then(() => {
        // only support for extension appUi or its extensions
        if (extension.id === Constants.ExtensionFolders.BASE) {
          return undefined;
        }

        const promises = [];
        // attempt to fetch the extension descriptor for the current extension, and all its dependencies.
        const re = application.runtimeEnvironment;
        promises.push(loadDescriptor(extension, re));
        // unfortunately calling extension.getRequiredExtensions() here fails many tests because we require better
        // mockExtension support (for dependencies)
        (Object.keys(extension.dependencies || {})).forEach((depExtId) => {
          const depExtension = extension.registry && extension.registry.getExtensionById(depExtId);
          promises.push(loadDescriptor(depExtension, re));
        });

        return Promise.all(promises);
      });
    }

    /**
     * Given a container it returns the scope resolvers for the associated extension and all 'dependencies' extensions.
     * @param {Container} container
     * @returns {Object|undefined} an Object with scope name to scope resolver object, or undefine. The scope resolver
     * object for an extension is a simple class that provdides just the requisite methods to resolve the type.
     *
     */
    static getExtensionScopeResolverMap(container) {
      const c = container;
      const selfExtId = c.extensionId;
      const selfExt = c.extension;
      // only for extensions add scope resolvers for resolving types exposed by each extension
      if (c.isExtensionAppUi() && selfExtId !== Constants.ExtensionFolders.BASE) {
        const map = {};

        // pull in all dependent extensions' scope resolvers; extension types can be from any of these
        const { extension } = c;
        extension.getRequiredExtensions().forEach((depExt) => {
          map[depExt.id] = new ExtensionScope(depExt, c);
        });

        const selfExtScope = new ExtensionScope(selfExt, c);
        return Object.assign({ [selfExtId]: selfExtScope, [Constants.SELF_EXTENSION_PREFIX]: selfExtScope }, map);
      }

      return undefined;
    }

    /**
     * Logs a warning if resourcePath used for an extension type accesses a resource that is not an extension resource.
     * @param {Container} container can be undefined for action chain scope
     * @param {string} varName name of variable
     * @param {Object} variableDef the variable definition
     * @param {string} resourcePath resource path to load
     * @return {string} the resourcePath as is, after logging warning where applicable
     */
    static checkTypeResourceAccess(container, varName, variableDef, resourcePath) {
      const typeRef = variableDef.type;
      const { extId, scope, name } = StateUtils.parseType(typeRef);

      // if the resource references a resource that is outside of the extension resources, log a warning. This is a
      // guard for vx/ based resource paths. Unclear if we want extension types to refer to appUi resources
      if (extId && container && resourcePath && resourcePath.startsWith(Constants.EXTENSION_PATH)) {
        const msg = `The extension type '${typeRef}' on variable '${varName}' references a resource '${resourcePath}'`
              + ' that is part of an AppUi. This is not a recommended configuration.';

        if (typeRef.startsWith(`${Constants.SELF_EXTENSION_PREFIX}/:`) && checkInvalidPath(resourcePath)) {
          logger.warn(msg);
        } else if (extId !== Constants.SELF_EXTENSION_PREFIX && scope === Constants.THIS_PREFIX) {
          // if we are here, extId is either 'base' or a specific extension id where the scope is 'this'.
          // For 'base/:typeRef', this can only refer to the base scope, so nothing to check.
          // For <ext-id>/:typeRef, the typeRef could be defined in
          // - the current (container) scope
          // - or its base scope,
          // - or the extension scope, for the referred extension
          //
          if (extId === (container.base && container.base.extensionId)) {
            const thisScopeResolver = container.scopeResolver[Constants.THIS_PREFIX];
            const baseScopeResolver = container.base.scopeResolver[Constants.THIS_PREFIX];
            if (thisScopeResolver.hasType(name) || baseScopeResolver.hasType(name)) {
              return resourcePath;
            }
            // we are dealing with an extension type
            if (checkInvalidPath(resourcePath)) {
              logger.warn(msg);
            }
          }
        }
      }
      return resourcePath;
    }
  }

  return ExtensionViewModel;
});

